import React, { useRef, useState, useCallback, useEffect } from 'react';
import classnames from 'classnames';
import { useIsOverflowed } from '@wix/da-hooks/pkg/useIsOverflowed';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import { DEFAULT_COLLAPSED_HEIGHT } from '../../constants';

import s from './ExpandableWrapper.scss';

const NO_TOGGLE_HEIGHT_ADJUST = 32;

export interface Props {
  /** optionally pass in a ref that will be set to the div wrapped around the children */
  contentContainerRef?: React.RefObject<HTMLElement | null>;
  /** max height in px of wrapper when not expanded */
  collapsedHeight?: number;
  /** If false, don't expand when the clickable area is pressed */
  allowExpansion?: boolean;
  /** Force the toggle button to be shown even if the content is not
   *  tall enough to cause the wrapper to be collapsible */
  forceShowToggle?: boolean;
  toggleButton?: React.ReactNode;
  toggleButtonHref?: string;
  toggleButtonBiData?: BiEvent;
  toggleButtonContents?: React.ReactNode;
  /** unique string for this content (deviationId, etc.) */
  overflowCacheKey?: string;
  /** called when the content div is expanded or collapsed */
  onExpansion?: (isExpanded?: boolean) => void;
  /** called when the content div height has been calculated */
  onOverflowMeasured?: () => void;
  children?: React.ReactNode;
}

const ExpandableWrapper: React.FC<Props> = ({
  contentContainerRef: propsOverflowRef,
  collapsedHeight = DEFAULT_COLLAPSED_HEIGHT,
  allowExpansion,
  forceShowToggle,
  toggleButton,
  toggleButtonHref,
  toggleButtonBiData,
  toggleButtonContents,
  overflowCacheKey,
  onExpansion,
  onOverflowMeasured,
  children,
}) => {
  const defaultOverflowRef = useRef<HTMLDivElement>(null);
  const contentContainerRef = propsOverflowRef || defaultOverflowRef;
  const { isOverflowed, isReady: isOverflowMeasured } = useIsOverflowed(
    contentContainerRef,
    undefined,
    overflowCacheKey
  );
  useEffect(() => {
    isOverflowMeasured && onOverflowMeasured && onOverflowMeasured();
  }, [isOverflowMeasured, onOverflowMeasured]);

  const [expanded, setExpanded] = useState(false);
  const toggleExpanded = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      setExpanded(value => {
        onExpansion && onExpansion(value);
        return !value;
      });
    },
    [onExpansion]
  );

  const collapsedHeightLimit = toggleButtonContents
    ? collapsedHeight
    : collapsedHeight - NO_TOGGLE_HEIGHT_ADJUST;
  const isShowingToggleButton = (isOverflowed && !expanded) || forceShowToggle;
  return (
    <div
      className={classnames(s['root'], !expanded && s['collapsed'])}
      {...(!expanded && { style: { maxHeight: collapsedHeightLimit } })}
    >
      <div className={s['content']} ref={contentContainerRef as any}>
        {children}
      </div>

      {isShowingToggleButton &&
        (toggleButton || (
          <TextButton
            className={classnames(s['toggle-button'], !toggleButtonContents && s['no-button-text'])}
            variant="main"
            size="medium"
            onClick={allowExpansion ? toggleExpanded : undefined}
            href={toggleButtonHref}
            isFakeButton={!allowExpansion && !toggleButtonHref}
            biData={toggleButtonBiData}
            noHorizontalPaddings={false}
          >
            <span className={s['toggle-button-contents']}>{toggleButtonContents}</span>
          </TextButton>
        ))}
    </div>
  );
};
ExpandableWrapper.displayName = 'ExpandableWrapper';
export default ExpandableWrapper;
